import { Query, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { TRequestOptions } from '@/shared/api';
import {
  getChannelById,
  getChannelLogsByChannelId,
  GetChannelLogsByChannelIdParams,
  getChannels,
  getExternalChannelById,
  IChannelDetailsDto,
  IChannelsListResponseDto,
  IChannelsParamsDto,
  IExternalChannelQrResponseDTO,
  Log,
} from '@/shared/api/channels';

import { LogsFormValues } from './model/types';

const CHANNEL_QUERY_KEY = 'channel';
const refetchDelay = 10000;
const syncingRefetchDelay = 3000;
const syncingStatuses = new Set(['authorized', 'contacts_synced', 'chats_synced']);

export const isSyncingChannelStatus = (status?: string) => {
  return !!status && syncingStatuses.has(status);
};

const hasSyncingChannel = (data?: IChannelsListResponseDto) => {
  return data?.data?.some((channel) => isSyncingChannelStatus(channel.status)) ?? false;
};

export const channelsListQuery = (
  params?: IChannelsParamsDto,
  options?: TRequestOptions
) => ({
  queryKey: [CHANNEL_QUERY_KEY, 'list'],
  queryFn: (): Promise<IChannelsListResponseDto> =>
    getChannels(params, options).then((r) => r),
  refetchInterval: (query: Query<IChannelsListResponseDto>) => {
    if (hasSyncingChannel(query.state.data)) {
      return syncingRefetchDelay;
    }

    return refetchDelay;
  },
});

export const logsListQuery = (channelId: string, form: LogsFormValues) => ({
  queryKey: [CHANNEL_QUERY_KEY, 'list', 'logs', channelId, form],
  queryFn: async ({ pageParam }: { pageParam?: string }): Promise<Log[]> => {
    let toDate = form.to ? form.to : pageParam;

    // Если есть фильтр to и передается pageParam, проверяем, не вышли ли мы за границы
    if (form.to && pageParam) {
      const filterToDate = dayjs(form.to);
      const pageParamDate = dayjs(pageParam);

      // Если pageParam раньше или равен фильтру to, используем pageParam
      if (
        pageParamDate.isBefore(filterToDate) ||
        pageParamDate.isSame(filterToDate)
      ) {
        toDate = pageParam;
      }
    }

    if (form.to) {
      // Для пагинации: используем pageParam и вычитаем 1 мс
      toDate = dayjs(form.to)
        .add(1, 'minute')
        .subtract(1, 'millisecond')
        .toISOString();
    } else if (pageParam) {
      // Для пользовательского фильтра: используем как есть (включительно)
      toDate = dayjs(pageParam).subtract(1, 'millisecond').toISOString();
    }

    const params: GetChannelLogsByChannelIdParams = {
      count: 20,
      from: form.from ?? undefined,
      to: toDate,
      severity: form.severity ?? undefined,
    };

    const response = await getChannelLogsByChannelId(channelId, params);
    return response ?? [];
  },
  getNextPageParam: (
    lastPage: Log[],
    allPages: Log[][]
  ): string | undefined => {
    if (lastPage.length === 0) {
      return undefined;
    }

    const lastEventDate = dayjs(lastPage[lastPage.length - 1].event_time);

    // Если указан фильтр to, проверяем достижение границы
    if (form.to) {
      const filterToDate = dayjs(form.to);
      const firstTime = dayjs(lastPage[0].event_time);
      const lastFirstTime = dayjs(allPages[allPages.length - 1][0].event_time);

      if (firstTime.isSame(lastFirstTime)) return undefined;
      // Если последнее событие уже за пределами фильтра, прекращаем
      if (lastEventDate.isAfter(filterToDate)) {
        return undefined;
      }

      // Если последнее событие равно фильтру to, это может быть граница
      if (lastEventDate.isSame(filterToDate)) {
        // Проверяем, есть ли еще события с такой же датой на предыдущих страницах
        // Если это маловероятно, можно просто прекратить пагинацию
        return undefined;
      }
    }

    return lastPage[lastPage.length - 1].event_time;
  },
  initialPageParam: undefined,
  retry: 2,
});

export const channelByIdQuery = (
  channelId: string,
  options?: TRequestOptions
) => ({
  queryKey: [CHANNEL_QUERY_KEY, 'byId', channelId],
  queryFn: (): Promise<IChannelDetailsDto> =>
    getChannelById(channelId, options).then((r) => r),
});

export const refetchWhileChannelSyncing = (query: Query<IChannelDetailsDto>) => {
  return isSyncingChannelStatus(query.state.data?.status)
    ? syncingRefetchDelay
    : false;
};

export const externalChannelByIdQuery = (
  channelId: string,
  options?: TRequestOptions
) => ({
  queryKey: [CHANNEL_QUERY_KEY, 'byId', channelId, 'external'],
  queryFn: (): Promise<IExternalChannelQrResponseDTO> =>
    getExternalChannelById(channelId, options).then((r) => r),
});

export const useInvalidateChannelById = () => {
  const queryClient = useQueryClient();

  return (channelId: string) =>
    queryClient.invalidateQueries({
      queryKey: [CHANNEL_QUERY_KEY, 'byId', channelId],
    });
};

export const useOptimisticUpdateChannelById = () => {
  const queryClient = useQueryClient();

  return (data: IChannelDetailsDto) =>
    queryClient.setQueryData([CHANNEL_QUERY_KEY, 'byId', data.id], { ...data });
};

export const useInvalidateChannelsList = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [CHANNEL_QUERY_KEY, 'list'],
    });
};
